export default (componentId) => ({
  componentId,
  query: '',
  // Grouped catalog: the selected section key and, in the two-pane slide
  // (a catalog container narrower than 36rem, decided purely in CSS), which
  // pane is showing; the side-by-side layout ignores `pane`. Both survive an
  // inline editor interaction; a fresh open starts on the section list again.
  section: null,
  pane: 'sections',
  view: 'closed',
  expandedFilters: [],
  hydratedFilters: [],
  focusFilter: null,
  pendingEditors: {},
  loadingFilter: null,
  loadingLabel: '',
  requestSequence: 0,
  pendingRequestId: null,
  trigger: null,
  serverClosePending: false,
  editorCancelPending: false,
  // Combined draft result count: the label shared Apply shows once a
  // preview answered (null = no count from the browser yet), whether the last
  // answer came without a count (the button then shows plain Apply instead of
  // the count the server rendered for the draft the editor opened with), the
  // debounce timer, and a sequence so a slow answer never overwrites a newer
  // draft's count.
  previewLabel: null,
  previewUnavailable: false,
  previewPending: false,
  previewTimer: null,
  previewSequence: 0,

  openPanel(view = 'chooser') {
    const dialog = this.$refs.dialog

    if (!dialog) {
      return
    }

    // A fresh panel starts from its server-rendered preview label.
    if (this.view === 'closed') {
      this.resetPreviewCount()
    }

    this.view = 'chooser'

    if (!dialog.open) {
      this.trigger = document.activeElement
      this.query = ''
      this.pane = 'sections'
      dialog.showModal()
      this.$nextTick(() => this.focusPanel(dialog))
    }

  },

  showChooser() {
    this.openPanel('chooser')
  },

  returnToChooser(wire) {
    const hadServerEditor = this.expandedFilters.length > 0 || this.hydratedFilters.length > 0
      || Object.keys(this.pendingEditors).length > 0
      || Boolean(this.$refs.dialog?.querySelector('.resourcerer-filter-editor'))

    this.resetInlineEditors()
    this.openPanel('chooser')

    // cancelFilterPanel() only discards staged server state; it never answers
    // with a close event, so a re-entered chooser cannot be closed late.
    if (hadServerEditor) {
      wire?.cancelFilterPanel?.()
    }
  },

  toggleEditor(queryName, label, wire, sectionKey = null) {
    if (this.expandedFilters.includes(queryName)) {
      this.expandedFilters = this.expandedFilters.filter(name => name !== queryName)
      if (this.focusFilter === queryName) this.focusFilter = null
      return
    }

    this.requestEditor(queryName, label, wire, sectionKey)
  },

  requestEditor(queryName, label, wire, sectionKey = null) {
    this.openPanel()
    this.focusFilter = queryName
    const row = Array.from(this.$refs.dialog?.querySelectorAll?.('[data-filter-option]') ?? [])
      .find(element => element.dataset.filterOption === queryName)
    sectionKey ??= row?.dataset.filterOptionSection ?? null
    if (typeof sectionKey === 'string' && sectionKey !== '') {
      this.section = sectionKey
      this.pane = 'filters'
    }
    // Chips and quick triggers must reveal the corresponding catalog row,
    // including when a previous search would otherwise hide it.
    if (row && !this.rowVisible(sectionKey, row.textContent, null, [])) {
      this.query = ''
    }
    if (!this.expandedFilters.includes(queryName)) {
      this.expandedFilters = [...this.expandedFilters, queryName]
    }
    if (this.pendingEditors[queryName] !== undefined) {
      return
    }
    const editor = Array.from(this.$refs.dialog?.querySelectorAll?.('[data-filter-inline-editor]') ?? [])
      .find(element => element.dataset.filterInlineEditor === queryName)
    if (this.hydratedFilters.includes(queryName) && editor) {
      this.focusEditor(queryName)
      return
    }
    // A public server reset can remove the hydrated markup without a row click.
    this.hydratedFilters = this.hydratedFilters.filter(name => name !== queryName)

    const requestId = ++this.requestSequence
    this.pendingEditors = { ...this.pendingEditors, [queryName]: requestId }
    this.pendingRequestId = requestId
    this.loadingFilter = queryName
    this.loadingLabel = label || queryName
    let request
    try {
      request = wire?.openFilterEditor?.(queryName, requestId)
    } catch {
      this.handleRequestFailure(requestId)
      return
    }
    Promise.resolve(request).catch(() => this.handleRequestFailure(requestId))
  },

  focusEditor(queryName) {
    this.$nextTick(() => {
      if (!this.$refs.dialog?.open || !this.expandedFilters.includes(queryName) || this.focusFilter !== queryName) {
        return
      }
      const editor = Array.from(this.$refs.dialog.querySelectorAll?.('[data-filter-inline-editor]') ?? [])
        .find(element => element.dataset.filterInlineEditor === queryName)
      const control = editor?.querySelector('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])')
      control?.focus({ preventScroll: true })
      // A quick trigger may target a row below the current scroll position.
      editor?.scrollIntoView?.({ block: 'nearest' })
    })
  },

  resetInlineEditors() {
    this.expandedFilters = []
    this.hydratedFilters = []
    this.focusFilter = null
    this.invalidatePendingRequest()
    this.resetPreviewCount()
  },

  requestEditorFromEvent(event, wire) {
    if (!this.handles(event)) {
      return
    }

    const { queryName, label } = event.detail

    if (typeof queryName !== 'string' || queryName === '') {
      return
    }

    this.requestEditor(queryName, label, wire)
  },

  handleRequestFailure(requestId) {
    const queryName = Object.keys(this.pendingEditors)
      .find(name => this.pendingEditors[name] === requestId)
    if (queryName === undefined) {
      return
    }
    this.finishEditorRequest(queryName)
    this.expandedFilters = this.expandedFilters.filter(name => name !== queryName)
  },

  finishEditorRequest(queryName) {
    const pending = { ...this.pendingEditors }
    delete pending[queryName]
    this.pendingEditors = pending
    if (this.loadingFilter === queryName) {
      this.pendingRequestId = null
      this.loadingFilter = null
      this.loadingLabel = ''
    }
  },

  handleServerOpen(event) {
    if (!this.handles(event)) {
      return
    }
    const { queryName, requestId, view, reveal } = event.detail
    if (reveal === true && !this.hydratedFilters.includes(queryName)) {
      return
    }
    if (Number.isInteger(requestId)) {
      if (this.view === 'closed' || this.pendingEditors[queryName] !== requestId) {
        return
      }
      this.finishEditorRequest(queryName)
    } else {
      if (view !== 'editor') this.resetInlineEditors()
      this.openPanel()
      this.focusFilter = queryName ?? null
      if (view === 'editor') {
        const row = Array.from(this.$refs.dialog?.querySelectorAll?.('[data-filter-option]') ?? [])
          .find(element => element.dataset.filterOption === queryName)
        if (row?.dataset.filterOptionSection) {
          this.section = row.dataset.filterOptionSection
          this.pane = 'filters'
        }
        this.query = ''
      }
    }
    if (view === 'editor' && typeof queryName === 'string') {
      if (!this.hydratedFilters.includes(queryName)) {
        this.hydratedFilters = [...this.hydratedFilters, queryName]
      }
      // Collapsing during loading must remain collapsed on completion.
      if (!Number.isInteger(requestId) && !this.expandedFilters.includes(queryName)) {
        this.expandedFilters = [...this.expandedFilters, queryName]
      }
      this.focusEditor(queryName)
    } else if (typeof queryName === 'string') {
      this.expandedFilters = this.expandedFilters.filter(name => name !== queryName)
    }
  },

  handleServerClose(event) {
    if (!this.handles(event) || this.hydratedFilters.length === 0) {
      return
    }
    this.closePanel(true)
  },

  invalidatePendingRequest() {
    this.pendingEditors = {}
    this.pendingRequestId = null
    this.loadingFilter = null
    this.loadingLabel = ''
  },

  focusPanel(dialog = this.$refs.dialog) {
    if (!dialog?.open) {
      return
    }

    const scope = dialog.querySelector(`[data-filter-panel-view="${this.view}"]`) || dialog
    const editorControl = scope.querySelector?.([
      '.resourcerer-filter-editor input:not([disabled])',
      '.resourcerer-filter-editor select:not([disabled])',
      '.resourcerer-filter-editor textarea:not([disabled])',
      '.resourcerer-filter-editor button:not([disabled])',
    ].join(', '))
    // This is the only focus authority: the search input carries no native
    // `autofocus`, because showModal() would honor it synchronously before this
    // runs. On coarse pointers a focused search input raises the on-screen
    // keyboard over the list, so the first control receives focus instead.
    const search = this.prefersCoarsePointer() ? null : scope.querySelector?.('[data-filter-search]')
    const firstControl = scope.querySelector?.('button:not([disabled]), input:not([disabled])')

    ;(editorControl || search || firstControl)?.focus()
  },

  // Editor draft changed: ask the host for the count of the draft after the
  // user paused typing/picking. The draft travels as a call parameter — the
  // editor binds deferred, so there is no live sync to piggyback on.
  schedulePreviewCount(wire, queryName, draft, delay = 300) {
    clearTimeout(this.previewTimer)

    this.previewTimer = setTimeout(() => {
      this.previewTimer = null
      this.requestPreviewCount(wire, queryName, draft)
    }, delay)
  },

  schedulePanelPreviewCount(wire, drafts, delay = 300) {
    if (this.view === 'closed' || this.hydratedFilters.length === 0) {
      return
    }
    this.previewLabel = null
    this.previewUnavailable = true
    this.cancelPreviewCount()
    const snapshot = JSON.parse(JSON.stringify(drafts))
    this.previewTimer = setTimeout(() => {
      this.previewTimer = null
      const sequence = ++this.previewSequence
      if (this.view === 'closed' || this.hydratedFilters.length === 0) {
        return
      }
      this.previewPending = true
      let request
      try {
        request = wire?.previewFilterPanelResultCount?.(snapshot)
      } catch {
        this.settlePreviewCount(sequence, null)
        return
      }
      Promise.resolve(request)
        .then(result => this.settlePreviewCount(sequence, result))
        .catch(() => this.settlePreviewCount(sequence, null))
    }, delay)
  },

  requestPreviewCount(wire, queryName, draft) {
    const sequence = ++this.previewSequence

    if (this.view === 'closed' || !this.hydratedFilters.includes(queryName)) {
      return
    }

    this.previewPending = true

    let request

    try {
      request = wire?.previewFilterResultCount?.(queryName, draft)
    } catch {
      this.settlePreviewCount(sequence, null)

      return
    }

    Promise.resolve(request)
      .then(result => this.settlePreviewCount(sequence, result))
      .catch(() => this.settlePreviewCount(sequence, null))
  },

  settlePreviewCount(sequence, result) {
    if (sequence !== this.previewSequence) {
      return
    }

    this.previewPending = false
    // A host without a preview (or an invalid draft, or a failed request)
    // answers null: the button falls back to plain Apply — not to the label
    // the server rendered, which counted an earlier draft.
    this.previewLabel = typeof result?.label === 'string' ? result.label : null
    this.previewUnavailable = this.previewLabel === null
  },

  // The text of the Apply button: the latest count, plain Apply once a preview
  // answered without one, otherwise the label the server rendered at open.
  applyLabel(serverLabel, plainLabel) {
    if (this.previewLabel !== null) {
      return this.previewLabel
    }

    return this.previewUnavailable ? plainLabel : serverLabel
  },

  // Apply drops pending previews. Method-level Renderless also lets Livewire
  // render when a preview and Apply share a request.
  cancelPreviewCount() {
    clearTimeout(this.previewTimer)
    this.previewTimer = null
    this.previewSequence++
    this.previewPending = false
  },

  // Closing or resetting the panel forgets its preview label.
  resetPreviewCount() {
    this.cancelPreviewCount()
    this.previewLabel = null
    this.previewUnavailable = false
  },

  prefersCoarsePointer() {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && Boolean(window.matchMedia('(pointer: coarse)')?.matches)
  },

  closePanel(serverDriven = false) {
    const dialog = this.$refs.dialog

    this.view = 'closed'
    this.resetInlineEditors()

    if (dialog?.open) {
      this.serverClosePending = serverDriven
      dialog.close()
    }
  },

  dismissPanel(wire) {
    const dialog = this.$refs.dialog

    if (this.hasOpenNestedOverlay(dialog)) {
      return
    }

    const cancelServerEditor = this.expandedFilters.length > 0 || this.hydratedFilters.length > 0
      || Object.keys(this.pendingEditors).length > 0
      || Boolean(dialog?.querySelector('.resourcerer-filter-editor'))

    this.editorCancelPending = cancelServerEditor
    this.closePanel()

    if (cancelServerEditor) {
      wire?.cancelFilterPanel?.()
    }
  },

  handleClose(wire) {
    const dialog = this.$refs.dialog
    const cancelServerEditor = (
      !this.serverClosePending
      && !this.editorCancelPending
      && (
        this.expandedFilters.length > 0 || this.hydratedFilters.length > 0
        || Object.keys(this.pendingEditors).length > 0
        || Boolean(dialog?.querySelector('.resourcerer-filter-editor'))
      )
    )

    this.serverClosePending = false
    this.editorCancelPending = false
    this.view = 'closed'
    this.resetInlineEditors()
    this.restoreFocus()

    if (cancelServerEditor) {
      wire?.cancelFilterPanel?.()
    }
  },

  dismissBackdrop(event, wire) {
    if (event.target !== this.$refs.dialog) {
      return
    }

    this.dismissPanel(wire)
  },

  hasOpenNestedOverlay(dialog = this.$refs.dialog) {
    return Boolean(dialog?.querySelector(
      '[data-teleported-overlay][data-overlay-open="true"], .flatpickr-calendar.open',
    ))
  },

  restoreFocus() {
    if (this.trigger?.isConnected) {
      this.trigger.focus()
    }

    this.trigger = null
  },

  handles(event) {
    return event?.detail?.componentId === this.componentId
  },

  normalizeSearchValue(value) {
    return String(value)
      .toLocaleLowerCase()
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .replace(/[^\p{L}\p{N}]+/gu, '')
  },

  matches(...values) {
    const needle = this.normalizeSearchValue(this.query.trim())

    return needle === '' || values
      .flat(Infinity)
      .filter(value => value !== null && value !== undefined)
      .some(value => this.normalizeSearchValue(value).includes(needle))
  },

  filterMatches(title, section, terms = []) {
    return this.matches(title, section, terms)
  },

  filterMatchDetails(title, section, terms = []) {
    const needle = this.normalizeSearchValue(this.query.trim())

    if (needle === '' || this.matches(title, section)) {
      return ''
    }

    return terms.find(term => this.normalizeSearchValue(term).includes(needle)) || ''
  },

  sectionMatches(section, filters) {
    return this.matches(section, filters)
  },

  isSearching() {
    return this.query.trim() !== ''
  },

  // Grouped catalog: open one section's rows (the second slide pane in a
  // narrow container; in the side-by-side layout the nav simply highlights it).
  // In the slide the tapped nav button slides away, so focus follows to the
  // back header once it is rendered visible; the columns layout keeps that
  // button display:none and focus stays on the nav button.
  selectSection(sectionKey) {
    this.section = sectionKey
    this.pane = 'filters'

    this.$nextTick(() => this.focusVisible(this.$refs.dialog?.querySelector?.('[data-filter-section-back]')))
  },

  // Back to the section list: focus returns to the nav button of the section
  // that was open, so keyboard and screen-reader users land where they left.
  showSections() {
    this.pane = 'sections'

    this.$nextTick(() => this.focusVisible(this.sectionButton(this.section)))
  },

  sectionButton(sectionKey) {
    if (sectionKey === null || sectionKey === undefined) {
      return null
    }

    return Array.from(this.$refs.dialog?.querySelectorAll?.('[data-filter-section]') ?? [])
      .find(button => button.getAttribute('data-filter-section') === String(sectionKey)) ?? null
  },

  // Focus a slide control only while it is actually rendered visible: the
  // panes hide with display:none (columns layout, search) or visibility:hidden
  // (the slid-out pane), and focusing a hidden control would go nowhere or
  // scroll the clipped grid, so the current focus is kept instead.
  focusVisible(element) {
    if (!element) {
      return
    }

    const visible = typeof element.checkVisibility === 'function'
      ? element.checkVisibility({ visibilityProperty: true })
      : true

    if (visible) {
      element.focus({ preventScroll: true })
    }
  },

  sectionLabel(labels = {}) {
    return this.section !== null && Object.hasOwn(labels, this.section) ? labels[this.section] : ''
  },

  // Accessible name of the back header: the translated template with its
  // `:section` placeholder filled from the selected section's label.
  sectionBackLabel(template, labels = {}) {
    return String(template).replace(':section', this.sectionLabel(labels))
  },

  // Whether one chooser row is shown. Without a section (simple catalog) the
  // row only has to match the search; in a grouped catalog a search shows every
  // matching row across all sections, otherwise the selected section's rows.
  rowVisible(sectionKey, title, section, terms = []) {
    if (this.isSearching()) {
      return this.filterMatches(title, section, terms)
    }

    return sectionKey === null || sectionKey === undefined || this.section === sectionKey
  },

  // `entries` mirrors every chooser row as [title, section, terms]; the empty
  // state shows once a non-empty query matches none of them.
  catalogIsEmpty(entries = []) {
    return this.query.trim() !== '' && !this.matches(entries)
  },

  // Grouped catalog: search results stay grouped under their section headings.
  // `entries` are the section's rows as [title, section, terms]; its heading
  // shows while a query is present and at least one of those rows matches
  // (the section label counts, exactly as it does for the rows themselves).
  searchSectionVisible(entries = []) {
    return this.isSearching() && this.matches(entries)
  },
})
